import type { DeliveryNoteDetailDto } from "@hisense/shared";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(
    /[&<>"]/g,
    (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!,
  );

const na = (s: string | null | undefined, empty = "N/A") => {
  const v = (s ?? "").trim();
  return v ? esc(v) : empty;
};

const printDate = (ms: number | null | undefined) =>
  ms
    ? new Date(ms).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "N/A";

/**
 * Bản in Phiếu giao hàng (A4), theo bố cục DeliveryNote.js generatePrintContent.
 *
 * Đây là TỜ GIẤY tài xế đưa khách ký nhận, không phải màn hình tra cứu — nên nó
 * phải in được cho mọi vai trò đọc được phiếu, và phải có đủ khối chữ ký người
 * giao / người nhận cùng thông tin xe và tài xế.
 *
 * Bảng giữ đúng 8 cột của bản cũ, kèm dòng TOTALS cộng CẢ số đặt lẫn số giao —
 * chênh lệch giữa hai con số này chính là thứ người nhận đối chiếu tại chỗ.
 * Cột LOT thêm khi có số lô (P-Process): với hàng lỗi, tờ phiếu này là bằng
 * chứng lô nào đã giao ngày nào.
 */
export function buildDeliveryNoteHtml(dn: DeliveryNoteDetailDto) {
  const logoSrc = `${window.location.origin}/brand/hisense-logo.webp`;
  const hasLot = dn.items.some((it) => (it.lotNumber ?? "").trim() !== "");
  const units = new Map<string, { planned: number; received: number }>();
  for (const it of dn.items) {
    const key = it.unit || "?";
    const q = units.get(key) ?? { planned: 0, received: 0 };
    q.planned += it.orderedQty;
    q.received += it.deliveredQty;
    units.set(key, q);
  }
  const plannedSummary = [...units]
    .map(([unit, q]) => `${q.planned.toLocaleString()} ${esc(unit)}`)
    .join("<br>");
  const receivedSummary = [...units]
    .map(([unit, q]) => `${q.received.toLocaleString()} ${esc(unit)}`)
    .join("<br>");
  /**
   * Số cột ĐỨNG TRƯỚC ô tổng đầu tiên (Ordered): CTN, Item, Sub ref, Mô tả,
   * và Lot nếu có.
   *
   * Trước đây dòng TOTALS dùng `cols - 5` với cols = 8/9, tức nhãn chỉ trải 3
   * cột thay vì 4 — cả hàng tổng lệch sang trái một ô, nên số ĐÃ ĐẶT in ra
   * nằm dưới cột Mô tả và số ĐÃ GIAO nằm dưới cột Đã đặt. Người đọc bản in
   * thấy hai con số đúng nhưng đứng sai chỗ, tức là đọc sai.
   *
   * Đếm thẳng số cột thay vì cộng trừ một hằng số: cách cũ sai lặng lẽ, và
   * thêm một cột nữa là sai tiếp mà không ai biết.
   */
  const leadCols = 4 + (hasLot ? 1 : 0);

  const itemRows = dn.items
    .map((it, index) => {
      const zebra = index % 2 === 0 ? "white" : "#f5f5f5";
      // Giao thiếu so với đặt là chuyện người nhận phải thấy ngay khi ký.
      const short =
        dn.deliveryStatus === "Delivered" && it.deliveredQty < it.orderedQty;
      return `<tr style="background-color:${zebra};">
        <td class="c">${na(it.ctnNo, "-")}</td>
        <td class="c mono b">${na(it.itemNo, "-")}</td>
        <td class="c muted">${na(it.subRef, "-")}</td>
        <td>${esc(it.description ?? "")}</td>
        ${hasLot ? `<td class="c mono">${na(it.lotNumber, "-")}</td>` : ""}
        <td class="c">${it.orderedQty.toLocaleString("en-US")}</td>
        <td class="c b${short ? " short" : ""}">${it.deliveredQty.toLocaleString("en-US")}</td>
        <td class="c">${na(it.unit, "-")}</td>
        <td class="muted">${na(it.remarks, "-")}</td></tr>`;
    })
    .join("");

  const receiptNote = dn.events?.find(
    (e) => e.toStatus === "Delivered" && e.reason,
  )?.reason;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>DN ${esc(dn.dnNo)}</title>
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, "Helvetica Neue", sans-serif; color:#000; font-size:10px; margin:0; }
  .mono { font-family:"Courier New",monospace; font-variant-numeric:tabular-nums; }
  .c { text-align:center; } .r { text-align:right; } .b { font-weight:bold; }
  .muted { color:#555; } .short { color:#b3261e; }
  .top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px; }
  .logo { height:46px; }
  .title { text-align:right; }
  .title h1 { margin:0; font-size:22px; letter-spacing:2px; }
  .title .no { font-size:14px; font-weight:bold; }
  .title .st { font-size:9px; text-transform:uppercase; letter-spacing:1px; margin-top:2px; }
  .meta { display:flex; gap:14px; margin:10px 0; }
  .meta > div { flex:1; border:1px solid #000; padding:7px; }
  .meta .h { font-size:8px; letter-spacing:1px; text-transform:uppercase; border-bottom:1px solid #000; margin-bottom:4px; padding-bottom:2px; }
  .meta .n { font-weight:bold; font-size:11px; }
  table { width:100%; border-collapse:collapse; margin-top:8px; }
  th { background:#000; color:#fff; padding:5px 4px; font-size:8px; letter-spacing:.4px; text-transform:uppercase; }
  td { border:1px solid #bbb; padding:4px; vertical-align:top; }
  tfoot td { border:none; padding:4px; font-weight:bold; border-top:2px solid #000; }
  .remarks { margin-top:12px; border:1px solid #bbb; padding:7px; white-space:pre-wrap; }
  .sign { display:flex; gap:40px; margin-top:30px; }
  .sign > div { flex:1; }
  .sign .cap { font-size:8px; text-transform:uppercase; letter-spacing:.6px; color:#555; }
  .sign .line { border-top:1px solid #000; margin-top:42px; padding-top:3px; font-size:8px; text-transform:uppercase; letter-spacing:.6px; text-align:center; }
  .foot { margin-top:14px; border-top:1px solid #ddd; padding-top:5px; font-size:8px; color:#777; }
</style></head><body>
  <div class="top">
    <div>
      <img class="logo" src="${logoSrc}" alt="">
      <div class="b" style="margin-top:4px;">HISENSE BUTTON CO., LTD.</div>
    </div>
    <div class="title">
      <h1>DELIVERY NOTE</h1>
      <div class="no mono">${esc(dn.dnNo)}</div>
      <div class="st">${na(dn.deliveryStatus, "")}</div>
    </div>
  </div>

  <p><b>${dn.deliveryStatus === "Delivered" ? "Confirmed receipt" : "Shipment plan — receipt not finalized"}</b>${dn.receiverName ? ` · Receiver: ${esc(dn.receiverName)}` : ""}</p>
  <div class="meta">
    <div>
      <div class="h">Deliver to</div>
      <div class="n">${na(dn.billToName)}</div>
      <div>${na(dn.shipTo, "")}</div>
      <div>${dn.customerDept ? "Dept: " + esc(dn.customerDept) : ""}</div>
    </div>
    <div>
      <div class="h">Reference</div>
      <div><b>PI:</b> ${na(dn.piNo, "-")}</div>
      <div><b>Invoice:</b> ${na(dn.invoiceNo, "-")}</div>
      <div><b>Packing list:</b> ${na(dn.plNo, "-")}</div>
      <div><b>Date:</b> ${esc(printDate(dn.date))}</div>
      <div><b>Delivery date:</b> ${esc(printDate(dn.deliveryDate))}</div>
    </div>
    <div>
      <div class="h">Transport</div>
      <div><b>Mode:</b> ${na(dn.deliveryMode, "-")}</div>
      <div><b>Vehicle:</b> ${na(dn.vehicleNo, "-")}</div>
      <div><b>Driver:</b> ${na(dn.driverName, "-")}</div>
      <div><b>Phone:</b> ${na(dn.driverPhone, "-")}</div>
      <div><b>Responsible:</b> ${na(dn.respBy, "-")}</div>
    </div>
  </div>

  <table>
    <thead><tr>
      <th>CTN</th><th>Item</th><th>Sub ref</th><th>Description</th>
      ${hasLot ? "<th>Lot</th>" : ""}
      <th>Planned</th><th>Received</th><th>Unit</th><th>Remarks</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
    <tfoot><tr>
      <td class="r" colspan="${leadCols}">TOTALS</td>
      <td class="c mono">${plannedSummary}</td>
      <td class="c mono">${receivedSummary}</td>
      <td colspan="2"></td>
    </tr></tfoot>
  </table>

  ${receiptNote ? `<div class="remarks"><b>Receipt note:</b> ${esc(receiptNote)}</div>` : ""}
  ${dn.remarks ? `<div class="remarks"><b>Remarks:</b> ${esc(dn.remarks)}</div>` : ""}

  <div class="sign">
    <div><div class="cap">Goods delivered in good condition</div><div class="line">Delivered by</div></div>
    <div><div class="cap">Signature &amp; company stamp</div><div class="line">Received by</div></div>
    <div><div class="cap">Date</div><div class="line">Date received</div></div>
  </div>

  <div class="foot">Hisense Button · hisensebutton.toankhontech.com · printed ${esc(new Date().toLocaleString("en-GB"))}</div>
</body></html>`;

  return html;
}

export function printDeliveryNote(dn: DeliveryNoteDetailDto) {
  printHtml(buildDeliveryNoteHtml(dn));
}
